import {cn} from '@sollapay/ui';
import {Button, Icon, Typography} from '@sollapay/ui/components';
import {Trans, useTranslation} from 'react-i18next';

import type {FC} from 'react';

export interface AiExtractionStatusProps {
  /** True while at least one document is still being processed. */
  isProcessing: boolean;
  /** Number of AI suggestions currently visible in the form. */
  suggestionsCount: number;
  /** True once processing finished but at least one critical field is still unfilled. */
  isPartial?: boolean;
  /** Stops AI generation — rendered as a button only while `isProcessing`. */
  onFillManually: () => void;
}

/**
 * AI extraction status banner.
 *
 * - Hidden when not processing AND no suggestions AND not partial.
 * - While processing: shows a "fill manually" button, generating copy, and a progress hairline.
 * - Once suggestions land: swaps to a completed state — success (check icon) or partial
 *   (warning-tinted icon + copy) depending on `isPartial`.
 */
export const AiExtractionStatus: FC<AiExtractionStatusProps> = ({
  isProcessing,
  suggestionsCount,
  isPartial = false,
  onFillManually
}) => {
  const {t} = useTranslation('common');

  if (!isProcessing && suggestionsCount === 0 && !isPartial) return null;

  return (
    <div
      className="relative flex items-start justify-end gap-4 overflow-hidden rounded-xl border border-border bg-background-surface p-4 shadow-xs"
      aria-live="polite"
      aria-atomic="true"
    >
      <Icon
        name={isProcessing ? 'sollapay:sparkles' : 'sollapay:check'}
        className={cn('size-10 shrink-0', !isProcessing && isPartial && 'text-fg-warning')}
        aria-hidden
      />

      <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-start">
        <Typography size="sm" weight="semibold" color="secondary">
          {isProcessing ? (
            t('aiExtraction.banner.generatingTitle')
          ) : isPartial ? (
            t('aiExtraction.banner.partialTitle')
          ) : (
            <Trans
              ns="common"
              i18nKey="aiExtraction.banner.completedTitle"
              count={suggestionsCount}
            />
          )}
        </Typography>
        <Typography size="sm" color="tertiary">
          {isProcessing
            ? t('aiExtraction.banner.generatingSubtitle')
            : isPartial
              ? t('aiExtraction.banner.partialSubtitle')
              : t('aiExtraction.banner.completedSubtitle')}
        </Typography>
      </div>

      {isProcessing && (
        <Button type="button" variant="secondary" size="sm" onClick={onFillManually}>
          {t('aiExtraction.banner.fillManually')}
        </Button>
      )}

      <div className="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden bg-border-subtle">
        <div
          className={cn(
            'absolute inset-y-0 bg-brand-primary',
            isProcessing ? 'left-0 w-1/2 animate-ai-progress' : 'inset-x-0'
          )}
        />
      </div>
    </div>
  );
};
